
/*
Miglioramenti:

Codifica URL: Utilizzo di encodeURIComponent per garantire che i valori dei cookie siano correttamente codificati.
Opzioni predefinite: L'oggetto options ha valori predefiniti per una maggiore flessibilità.
Consenso granularizzato: Il commento in setCookieConsent suggerisce di gestire consensi più specifici per categorie di cookie.
Aggiornamento dei cookie: Il commento in setCookieConsent sottolinea l'importanza di aggiornare i cookie esistenti in base al nuovo consenso.
Considerazioni aggiuntive:

Sicurezza: Per una maggiore sicurezza, considera l'utilizzo di HttpOnly per i cookie che non devono essere accessibili da JavaScript.
Privacy: Assicurati di rispettare le normative sulla privacy e di fornire all'utente un modo chiaro per revocare il consenso.
Librerie esterne: Potresti valutare l'utilizzo di librerie specializzate per la gestione dei cookie, come js-cookie, che offrono funzionalità aggiuntive e una maggiore astrazione.
Test: Scrivi test unitari per verificare il corretto funzionamento della classe.
*/

import { htmldoc } from "../../../base/lib/angular/platformreference";


interface CookieOptions {
  expires?: Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}


export class CookieManager {
  private readonly DEVICE_ID_STORAGE_KEY = 'deviceId';
  // ... altri metodi

  /**
   * Imposta un cookie.
   * 
   * @param name Nome del cookie
   * @param value Valore del cookie
   * @param options Opzioni aggiuntive (es. expires, path, domain, secure)
   */
  setCookie(name: string, value: string, options?: CookieOptions): void {
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
    if (options?.expires instanceof Date) {
      cookieString += `; expires=${options.expires.toUTCString()}`;
    }
    if (options?.path) {
      cookieString += `; path=${options.path}`;
    }
    if (options?.domain) {
      cookieString += `; domain=${options.domain}`;
    }
    if (options?.secure) {
      cookieString += `; secure`;
    }
    if (options?.sameSite) {
      cookieString += `; SameSite=${options.sameSite}`;
    }
    htmldoc().cookie = cookieString;
  }

  /**
   * Ottiene il valore di un cookie.
   * 
   * @param name Nome del cookie
   */
  getCookie(name: string): string | undefined {
    // ... implementazione
    const cookies = htmldoc().cookie.split(';');
    for (const cookie of cookies) {
      const parts = cookie.trim().split('=');
      if (parts[0] === name) {
        return decodeURIComponent(parts[1]);
      }
    }
    return undefined;
  }

  /**
   * Elimina un cookie.
   * 
   * @param name Nome del cookie
   * @param options Opzioni aggiuntive (es. path, domain)
   */
  deleteCookie(name: string, options?: CookieOptions) {
    this.setCookie(name, '', { ...options, expires: new Date(0) });
  }

  /**
   * Imposta un valore nel local storage.
   * 
   * @param key Chiave del valore
   * @param value Valore da salvare
   */
  setLocalStorage(key: string, value: string) {
    localStorage.setItem(key, value);
  }

  /**
   * Ottiene un valore dal local storage.
   * 
   * @param key Chiave del valore
   */
  getLocalStorage(key: string): string | null {
    return localStorage.getItem(key);
  }

  /**
   * Elimina un valore dal local storage.
   * 
   * @param key Chiave del valore
   */
  removeLocalStorage(key: string) {
    localStorage.removeItem(key);
  }

  /**
   * Controlla se l'utente ha dato il consenso all'uso dei cookies.
   * 
   * **Osservazioni:**
   * - Il metodo potrebbe leggere un flag booleano dal local storage o da un cookie.
   * - Potrebbe essere necessario implementare una logica più complessa per gestire diversi tipi di consenso (es. cookie necessari, cookie di performance, cookie di marketing).
   */
  hasCookieConsent(): boolean {
    return this.getLocalStorage('cookieConsent') === 'true';
  }

  /**
   * Imposta il consenso dell'utente all'uso dei cookies.
   * 
   * **Osservazioni:**
   * - Il metodo potrebbe impostare un flag booleano nel local storage o in un cookie.
   * - Potrebbe essere necessario aggiornare i cookie esistenti in base al nuovo consenso.
   */
  setCookieConsent(consent: boolean) {
    this.setLocalStorage('cookieConsent', consent.toString());
    // Aggiorna i cookie esistenti in base al nuovo consenso (se necessario) 
  }

  // ... altri metodi per gestire categorie di cookie, scadenze personalizzate, ecc.

  public generateDeviceId(): string {
    return [...Array(12)]
      .map(() => Math.random().toString(36).charAt(2))
      .join('');
  }

  getDeviceId(): string {
    let deviceId = localStorage.getItem(this.DEVICE_ID_STORAGE_KEY);

    if (!deviceId) {
      deviceId = this.generateDeviceId();
      localStorage.setItem(this.DEVICE_ID_STORAGE_KEY, deviceId);
    }

    return deviceId;
  }
}

/*
  Esempio di utilizzo
 
      const cookieManager = new CookieManager();

      // Imposta un cookie
      cookieManager.setCookie('username', 'johnDoe', { expires: new Date(Date.now() + 3600000) });

      // Ottieni un cookie
      const username = cookieManager.getCookie('username');

      // Controlla il consenso
      if (cookieManager.hasCookieConsent()) {
      // Accedi a funzionalità riservate
      }
*/